import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LogOut } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import './LogoutDialog.css';

/**
 * The one sign-out confirmation, opened from the top bar's profile menu, so leaving the CRM always
 * asks once, the same way. Drawn into <body>: the bars and the drawer have a backdrop filter or a
 * transform, which would otherwise pin its fixed overlay to their own box. On phones it is a
 * bottom sheet. Escape and the Cancel button close it, Tab stays inside it, and focus goes back to
 * where it was.
 */
function LogoutDialog({ open, onClose, onConfirm }) {
  const { t } = useI18n();
  const panelRef = useRef(null);
  const cancelRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    if (cancelRef.current) cancelRef.current.focus({ preventScroll: true });

    // Capture phase: Escape closes only this dialog, not whatever is open beneath it.
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const buttons = Array.from(panelRef.current.querySelectorAll('button:not([disabled])'));
      if (!buttons.length) return;
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      const inside = panelRef.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (opener && opener !== document.body && opener.isConnected && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      }
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="logout-overlay" onClick={() => closeRef.current()} role="presentation">
      <div
        ref={panelRef}
        className="logout-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="logout-dialog-icon" aria-hidden="true"><LogOut size={24} /></div>
        <h2 id="logout-dialog-title" className="logout-dialog-title">{t('settings.signOutQ')}</h2>
        <p id="logout-dialog-desc" className="logout-dialog-desc">{t('settings.signOutConfirm')}</p>
        <div className="logout-dialog-actions">
          <button
            ref={cancelRef}
            type="button"
            className="ds-btn ds-btn-secondary ds-btn-lg logout-dialog-btn"
            onClick={onClose}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="ds-btn ds-btn-danger-fill ds-btn-lg logout-dialog-btn"
            onClick={onConfirm}
          >
            {t('settings.signOut')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default LogoutDialog;
